<template>
  <Transition name="slide-down">
    <div v-if="showBanner" class="version-update-banner">
      <div class="version-update-banner__content">
        <div class="version-update-banner__message">
          <h3 class="version-update-banner__title">
            <span v-if="isInCreateEventFlow && eventWasSaved">✅ {{ $t('components.version-update-banner.event-saved') }}</span>
            <span v-else>{{ $t('components.version-update-banner.title') }}</span>
          </h3>
          <p class="version-update-banner__text">
            <span v-if="isInCreateEventFlow && eventWasSaved">Dit arrangement blev gemt. </span>
            {{ $t('components.version-update-banner.message') }}
          </p>
          <p v-if="isInCreateEventFlow && isSaving" class="version-update-banner__saving">
            ⚙️ Gemmer dit arrangement...
          </p>
          <p v-if="debugMode" class="version-update-banner__debug">
            Ny version: {{ versionStore.latestVersion }}
          </p>
        </div>
        <div class="version-update-banner__actions">
          <button 
            class="version-update-banner__reload"
            @click="reloadApp"
            :disabled="isSaving"
            :title="$t('components.version-update-banner.reload')">
            {{ $t('components.version-update-banner.reload') }}
          </button>
          <button 
            class="version-update-banner__dismiss" 
            @click="dismissBanner"
            :disabled="isSaving"
            :aria-label="$t('components.version-update-banner.dismiss')">
            ✕
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useVersionStore } from '@/stores/versionStore'
import { useNewEventStore } from '@/stores/newEventStore'
import { saveCriticalState } from '@/utils/stateRescue'

const versionStore = useVersionStore()
const newEventStore = useNewEventStore()
const route = useRoute()
const { updateAvailable, dismissed } = storeToRefs(versionStore)
const isSaving = ref(false)
const eventWasSaved = ref(false) // Track if event was actually saved

// Show banner if update is available and not dismissed
const showBanner = computed(() => {
  return updateAvailable.value && !dismissed.value
})

// Debug mode for testing (check if ?debug=true in URL)
const debugMode = computed(() => {
  return typeof window !== 'undefined' && new URL(window.location.href).searchParams.get('debug') === 'true'
})

// Check if we're in a create event flow
const isInCreateEventFlow = computed(() => {
  const routeName = route.name as string
  return routeName && ['createEventStep1', 'createEventStep2', 'createEventStep3'].includes(routeName)
})

// Auto-save event when version update is detected during create flow
// BUT: Only save if event already exists (has ID) OR we're on Step 2/3
// On Step 1, the event hasn't been created yet - we only preserve form state
watch(updateAvailable, async (newVal) => {
  if (newVal && isInCreateEventFlow.value && !isSaving.value) {
    const routeName = route.name as string
    const eventHasId = newEventStore.newEvent.id && newEventStore.newEvent.id !== 0
    const isStep2Or3 = routeName === 'createEventStep2' || routeName === 'createEventStep3'
    
    // Only save if event exists OR we're on step 2/3 (where save likely happened)
    if (eventHasId || isStep2Or3) {
      isSaving.value = true
      try {
        console.log('💾 Version update detected - auto-saving event', { routeName, eventId: newEventStore.newEvent.id })
        
        // CRITICAL FIX: Actually save the event to the database
        await newEventStore.saveEvent()
        
        eventWasSaved.value = true
        console.log('✅ Event auto-saved before version update')
      } catch (error) {
        console.error('❌ Failed to auto-save event during version update:', error)
        eventWasSaved.value = false
      } finally {
        isSaving.value = false
      }
    } else {
      console.log('ℹ️ Version update on Step 1 - skipping auto-save (event not created yet)')
      eventWasSaved.value = false
      console.log('ℹ️ Version update on Step 1 - skipping auto-save (event not created yet)')
    }
  }
})

const dismissBanner = () => {
  versionStore.dismissUpdate()
}

const reloadApp = () => {
  // Use sessionStorage for debugging since console.log gets wiped on reload
  sessionStorage.setItem('__reload_button_clicked__', 'true');
  sessionStorage.setItem('__reload_timestamp__', new Date().toISOString());
  
  // Always save state before reload if we have event data
  const routeName = route.name as string
  const inCreateFlow = routeName && ['createEventStep1', 'createEventStep2', 'createEventStep3'].includes(routeName)
  
  sessionStorage.setItem('__reload_route__', routeName || 'unknown');
  sessionStorage.setItem('__reload_in_create_flow__', String(inCreateFlow));
  
  if (inCreateFlow) {
    const stateSnapshot = {
        title: newEventStore.newEvent.title,
        typeId: newEventStore.newEvent.typeId,
        creatorId: newEventStore.newEvent.creatorId,
        creatorPartitionKey: newEventStore.newEvent.creatorPartitionKey,
        id: newEventStore.newEvent.id
    };
    sessionStorage.setItem('__reload_event_state__', JSON.stringify(stateSnapshot));
    
    // CRITICAL FIX: Pass the current route name from component context
    // useRouter() cannot be called inside saveCriticalState - it needs component context
    saveCriticalState(routeName)
    sessionStorage.setItem('__save_critical_state_called__', 'true');
  }
  
  // Hard reload to bypass browser cache
  window.location.href = window.location.href
}
</script>

<style scoped lang="scss">
@import '../../../scss/base';

.version-update-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background-color: $dn-v2-grass;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1rem 1.5rem;
    max-width: 100%;
  }

  &__message {
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin-bottom: 0.25rem;
    color: $dn-v2-dark-green;
    font-size: 1rem;
    font-weight: 600;
  }

  &__text {
    margin-bottom: 0;
    color: $dn-v2-dark-green;
    font-size: 0.95rem;
    line-height: 1.4;
  }

  &__saving {
    font-size: 0.85rem;
    color: $dn-v2-dark-green;
    margin-top: 0.5rem;
    margin-bottom: 0;
    font-weight: 500;
  }

  &__debug {
    font-size: 0.85rem;
    opacity: 0.85;
    margin-top: 0.5rem;
    color: $dn-v2-dark-green;
  }

  &__actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
    align-items: center;
  }

  &__reload {
    background-color: var(--theme-primary-button);
    color: var(--theme-primary-button-text);
    border: none;
    border-radius: 0.25rem;
    padding: 0.5rem 1rem;
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 500;
    font-size: 0.95rem;

    &:hover:not(:disabled) {
      opacity: 0.9;
      transform: scale(1.02);
    }

    &:active:not(:disabled) {
      transform: scale(0.98);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__dismiss {
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    padding: 0.25rem 0.5rem;
    color: $dn-v2-dark-green;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    line-height: 1;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      opacity: 0.7;
    }

    &:active:not(:disabled) {
      transform: scale(0.95);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

// Slide down animation
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
